import { IconInfoCircle } from "@tabler/icons-react";
import { Alert, BotonExportarExcel, Card, Group, Heading, Stack, Table, Tbody, Text, Th, Thead, Tr } from "@/components/ui";
import { useConfiguracionesAdmin } from "../hooks/use-configuraciones-admin";
import { ConfiguracionRow } from "./ConfiguracionRow";

/**
 * Título y bajada legibles por categoría (la clave cruda, ej. "ComprasSap", es la
 * sección de la ruta de IConfiguration · no describe nada por sí sola). Categoría
 * nueva sin entrada acá → cae al fallback (clave cruda en mayúsculas, sin bajada).
 */
const TITULOS_CATEGORIA: Record<string, { titulo: string; descripcion: string }> = {
  HistoricoDocumental: {
    titulo: "Histórico Documental",
    descripcion: "Exportación ZIP del acervo documental de las solicitudes de pago.",
  },
  PuePpd: {
    titulo: "Validación PUE/PPD",
    descripcion: "Reglas de recepción de CFDI según el método de pago (ventanas, retenciones).",
  },
  Sat: {
    titulo: "SAT · Validación Fiscal",
    descripcion: "Validador de comprobantes ante el SAT (proveedor real o mock, timeouts).",
  },
  ComprasSap: {
    titulo: "Órdenes de Compra y Contratos Anualizados",
    descripcion: "Origen de las Órdenes de Compra y Contratos Anualizados que llegan de SAP.",
  },
  Conciliacion: {
    titulo: "Conciliación de Orden de Compra",
    descripcion: "Tolerancias para el cruce entre el CFDI y la Orden de Compra.",
  },
  Email: {
    titulo: "Correo Electrónico",
    descripcion: "Envío de notificaciones por correo (SMTP real o mock por consola).",
  },
  Storage: {
    titulo: "Almacenamiento de Archivos",
    descripcion: "Raíz del sistema de archivos donde se guardan los documentos subidos.",
  },
  Polizas: {
    titulo: "Pólizas Contables",
    descripcion: "Depósito de las pólizas generadas al autorizar una solicitud (local y/o SFTP).",
  },
  Sftp: {
    titulo: "SFTP · Depósito Remoto",
    descripcion: "Credenciales y destino del servidor SFTP remoto para las pólizas.",
  },
};

function tituloDeCategoria(cat: string): { titulo: string; descripcion: string | null } {
  return TITULOS_CATEGORIA[cat] ?? { titulo: cat.toUpperCase(), descripcion: null };
}

/**
 * Gestor de configuraciones de la app (tabla administracion.configuraciones · fuente de
 * IConfiguration). Agrupa por categoría; cada valor es editable inline. El bootstrap
 * (cadena de conexión, logging) y los secretos (JWT) NO se listan · viven en appsettings.
 */
export function AdminConfiguracionesPage() {
  const { data: configuraciones } = useConfiguracionesAdmin();

  const categorias = [...new Set(configuraciones.map((c) => c.categoria))].sort();

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 md:px-6">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Heading order={2}>Configuraciones</Heading>
            <Text c="dimmed" size="sm">
              Ajustes de negocio de la aplicación · persistidos en base de datos. El bootstrap
              (cadena de conexión, logging) y los secretos (JWT) permanecen en appsettings.
            </Text>
          </div>
          <BotonExportarExcel
            archivo="configuraciones"
            hoja="Configuraciones"
            filas={configuraciones}
            columnas={[
              { header: "Categoría", value: (c) => c.categoria },
              { header: "Clave", value: (c) => c.clave },
              {
                header: "Valor",
                // Nunca se exporta el valor real de un secreto (contraseñas SMTP/SFTP,
                // passphrase) · el enmascarado en la UI sería cosmético si el Excel lo
                // revela de todas formas.
                value: (c) => (c.tipo === "Secreto" ? (c.valor ? "••••••••" : "") : c.valor),
              },
              {
                header: "Por defecto",
                value: (c) => (c.tipo === "Secreto" ? (c.valorDefault ? "••••••••" : "") : c.valorDefault),
              },
              { header: "Descripción", value: (c) => c.descripcion },
              { header: "Actualizado", value: (c) => c.actualizadoAt },
            ]}
          />
        </Group>

        <Alert variant="info" icon={<IconInfoCircle size={18} />}>
          Los cambios se guardan al instante. Algunos ajustes que seleccionan la implementación
          en el arranque (ej. <strong>Sat:Provider</strong>, <strong>ComprasSap:Provider</strong>) requieren
          reiniciar el servicio para tomar efecto.
        </Alert>

        {categorias.map((cat) => {
          const { titulo, descripcion } = tituloDeCategoria(cat);
          return (
            <Card key={cat} padding="lg">
              <Stack gap="sm">
                <div>
                  <Text fw={700} c="multiva" size="md">
                    {titulo}
                  </Text>
                  {descripcion && (
                    <Text c="dimmed" size="xs">
                      {descripcion}
                    </Text>
                  )}
                </div>
                {/* `layout="fixed"` es necesario para que las 4 columnas midan lo MISMO en
                    cada categoría: cada Card tiene su propia <table>, y sin layout fijo el
                    navegador recalcula el ancho de columna según el contenido de ESA tabla
                    (una descripción larga en un panel corría todo el resto). */}
                <Table verticalSpacing="xs" highlightOnHover layout="fixed" className="w-full">
                  <Thead>
                    <Tr>
                      <Th style={{ width: "34%" }}>Ajuste</Th>
                      <Th style={{ width: "30%" }}>Valor</Th>
                      <Th style={{ width: "21%" }}>Por defecto</Th>
                      <Th align="right" style={{ width: "15%" }}>Acciones</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {configuraciones
                      .filter((c) => c.categoria === cat)
                      .map((c) => (
                        <ConfiguracionRow key={c.clave} cfg={c} />
                      ))}
                  </Tbody>
                </Table>
              </Stack>
            </Card>
          );
        })}
      </Stack>
    </div>
  );
}
